export interface Recipe {
  id: string;
  title: string;
  subtitle?: string;
  type: 'food' | 'drink';
  cuisine: string;
  cookingTime: string;
  ingredients: string[];
  steps: string[];
  imageUrl?: string;
  date: string; // ISO format: '2025-11-09T14:30:00' or just '2025-11-09'
}

export const recipes: Recipe[] = [
  {
    id: '1',
    title: 'Corn Upma',
    subtitle: 'A dish that hits home. Corn upma is spicy flavorful food combined with comfort and ease. Truly, an early morning healthy breakfast, or late night meal.',
    type: 'food',
    cuisine: 'Indian',
    cookingTime: '10 minutes',
    date: '2025-11-01',
    ingredients: [
      'Creamed Corn (Tinned corn works)',
      'Curry Leaves',
      "Chilly: 1 chopped (any. Please **don't** substitute with Jalapenos)",
      'Mustard Seeds',
    ],
    steps: [
      'Turn on the heat. Let the pan get nice and hot.',
      'Once hot, pour 1 tbsp olive oil/vegetable oil. Let that heat slightly.',
      'Add in a handful of mustard seeds and roast till light brown.',
      'Add the chillies and curry leaves. Saute. Those aromatic fumes remind me of my kitchen back home.',
      'Once the chillies and curry leaves look slightly roasted (hard-darkish in color), add your creamed corn. If you don’t have creamed corn, just mash corn in some milk and let it stay for 10 minutes till the liquid absorbs the flavor and thickness of the corn.',
      ' ',
      '### Pro tip:',
      '* Take some extra oil, chilly, and curry leaves and sauté, and then garnish the oil with the ingredients on top of the corn.',

    ],
    imageUrl: '/images/food/Corn Upma.jpeg'
  },
  {
    id: '2',
    title: 'Squash Caramelle with Chanterelle Mushrooms and Miso Beurre Blanc',
    subtitle: 'A vegan twist on classic Italian filled pasta, featuring seasonal butternut squash and umami-rich miso.',
    type: 'food',
    cuisine: 'Italian-Fusion',
    cookingTime: '2 hours 45 minutes',
    date: '2025-10-28',
    ingredients: [
      '### Eggless Pasta Dough:',
      '300g (2 cups) \'00\' flour or all-purpose flour',
      '1/2 cup + 2-3 tablespoons warm water',
      '2 tablespoons olive oil',
      '1/2 teaspoon salt',
      '1 tablespoon aquafaba (chickpea water) for sealing pasta',
      '',
      '### Squash Filling:',
      '1 medium butternut squash (about 2 lbs)',
      '2 tablespoons olive oil',
      '1/2 cup nutritional yeast (replacing Parmigiano-Reggiano)',
      '1/4 teaspoon freshly grated nutmeg',
      '1 tablespoon white miso paste (adds umami)',
      'Salt and freshly ground black pepper',
      '1 tablespoon olive oil (for richness)',
      '',
      '### Miso Beurre Blanc (Vegan Version):',
      '1/4 cup white wine',
      '2 tablespoons white wine vinegar',
      '2 medium shallots, finely minced',
      '1/4 cup cashew cream (blend 1/2 cup soaked cashews with 1/4 cup water)',
      '2 tablespoons white miso paste',
      '225g vegan butter, cubed and cold',
      'Salt to taste',
      '',
      '### Mushroom Component:',
      '200g fresh chanterelle mushrooms, cleaned and torn into bite-sized pieces',
      '2 tablespoons vegan butter',
      '1 clove garlic, minced',
      '1 sprig fresh thyme',
      'Salt and freshly ground black pepper',
      '',
      '### Garnish:',
      '1/4 cup pumpkin seeds, toasted',
      'Fresh herbs or microgreens',
      'Extra virgin olive oil',
      'Flaky sea salt'
    ],
    steps: [
  '### Eggless Pasta Dough',
  'Mix flour and salt in a large bowl',  
  'Create a well in the center',  
  'Add olive oil and gradually add warm water while mixing',  
  'Knead for 15-20 minutes until smooth and elastic (slightly longer than egg dough)',  
  'Wrap in plastic and rest at room temperature for 45 minutes (longer rest needed for gluten development)',  
  'If dough feels too dry, add water 1 teaspoon at a time',  
  'If too sticky, add flour gradually',  
  '',
  '### Squash Filling',  
  'Preheat oven to 400°F (200°C)',  
  'Halve squash lengthwise, remove seeds',  
  'Drizzle with olive oil, season with salt and pepper',  
  'Roast cut-side down for 45-50 minutes until very tender',  
  'Scoop flesh into food processor',  
  'Add nutritional yeast, nutmeg, miso paste, olive oil, salt and pepper to taste',  
  'Pulse until very smooth',  
  'Cool completely before using',
  '',
  '### Assembly and Cooking',
  'Roll pasta dough to second-thinnest setting (may need more dusting flour than egg pasta)',
  'Cut into 2-inch squares',
  'Place 1 teaspoon filling in center of each square',
  'Brush edges with aquafaba',
  'Fold squares into rectangles, then twist ends to form candy shape',
  'Place on floured sheet pan, covered with towel',
  'Work quickly as eggless pasta can dry out faster',
  '',
  '### Notes',
  ' Pasta dough might need more kneading than egg pasta to develop proper gluten structure',
  ' Keep pasta covered while working to prevent drying',
  ' Aquafaba helps seal the pasta edges effectively',
  ' Can substitute cashew cream with other non-dairy creams',
  ' Fresh pasta will cook slightly faster than semolina pasta, about 1-2 minutes',
  ' If dough is crumbly, add water 1 teaspoon at a time',
  ' For best results, use pasta sheets immediately after rolling',
  '',
  'Serves 4-6 as a main course'
],
    imageUrl: '/images/food/Miso-Beurre.png'
  },
  {
    id: '3',
    title: 'Mishti Doi (Sweetened curd)',
    subtitle: 'A timeless, smooth, sweet curd from India — simple to make and utterly delicious.',
    type: 'food',
    cuisine: 'Dessert',
    cookingTime: 'Prep: 30 minutes; Fermentation: 10-12 hours',
    date: '2025-10-25',
    ingredients: [
      'Yogurt (starter) — a few tablespoons',
      'Milk (3.25% full-fat recommended; see note)',
      'Jaggery (to taste)',
      'Milk powder (optional — helps if using low-fat milk)',
    ],
    steps: [
      '### NOTE',
      "I tried this with 3.25% milk; if you prefer using 2% or lower, add 1-2 tablespoons of milk powder per cup of milk to improve body and creaminess.",
      '',
      '',
      'Strain your curd/yogurt for about 30 minutes to remove excess whey so the starter is thick.',
      'While the milk simmers, add milk powder if using (see note).',
      'Heat the milk on medium-high (about 6-7/10). Stir frequently to prevent film and scorching.',
      'Once milk comes close to simmering, remove any large bubbles/foam and make sure milk powder (if added) is fully dissolved and the milk is hot enough.',
      'Dissolve jaggery in a small amount of warm milk and then mix back into the main milk so it is evenly sweetened.',
      'Cool the milk to warm (roughly 40–45°C / 104–113°F), then add the thickened yogurt starter and mix gently but thoroughly.',
      'Pour into clean containers, cover, and keep in a warm place to ferment for 10–12 hours (longer for tangier result).',
      'After fermentation, refrigerate to chill and further set before serving.',
      '',
      'Serves 4–6'
    ],
    imageUrl: '/images/food/Mishti Doi.png'
  },
  {
    id: '4',
    title: 'Masala Laal Sharbat',
    subtitle: 'A refreshing spiced rose-flavored drink finished with coconut or sparkling water.',
    type: 'drink',
    cuisine: 'Indian',
    cookingTime: '5 minutes',
    date: '2025-10-22',
    ingredients: [
      "Laal sharbat (3 tbsp) — preferably Ralli's or Dabur",
      'Jaljeera powder (1 tbsp)',
      'Lemon (10 mL, freshly squeezed)',
      'Coconut water (to top up the glass)',
      'Ice cubes'
    ],
    steps: [
      'Add a few ice cubes to a tall glass.',
      'Pour the laal sharbat syrup into the glass.',
      'Add the jaljeera powder and squeeze in the lemon juice.',
      'Stir gently and let the mixture settle for a few seconds.',
      'Top up with coconut water (or sparkling water if preferred) and give a final stir.',
      'Serve immediately.'
    ],
    imageUrl: '/images/masala_laal_sharbat.jpg'
  },
  {
    id: '5',
    title: 'Gondhoraj Ghol',
    subtitle: 'A fragrant Bengali buttermilk cooler with the unmistakable citrus of Gondhoraj lime, perfect for Durga Puja.',
    type: 'drink',
    cuisine: 'Indian',
    cookingTime: '5 minutes',
    date: '2025-10-20',
    ingredients: [
      'Chilled plain yogurt – 1 cup',
      'Cold water – 2½ to 3 cups',
      'Powdered or caster sugar – 2–3 tbsp (adjust to taste)',
      'Black Salt – 1 pinch',
      'Black salt – a small pinch (optional)',
      'Gondhoraj lime – 1 (zest + juice)',  
      'Ice cubes – plenty'
    ],
    steps: [
      'In a large jug, whisk together the yogurt, sugar, and black salt until smooth.',
      'Add the Gondhoraj zest and juice.',
      'Pour in the cold water, stir well, and add lots of ice.',
      'Add some mint leaves and mix until frothy and serve immediately.'
    ],
    imageUrl: '/images/gondhoraj_ghol.jpg'
  },
  {
    id: '6',
    title: 'Crispy Salt and Pepper Oyster Mushrooms',
    subtitle: 'A fun addictive chinese starter that wont let you stop at one!',
    type: 'food',
    cuisine: 'Chinese',
    cookingTime: '20 minutes',
    date: '2025-10-18',
    ingredients: [
      'Oyster mushrooms – 500g',
      'Cornstarch – ½ cup',
      'Salt – 1 tsp',
      'Black pepper – 1 tsp',
      'Chinese 5 spice – ½ tsp',
      'Oil (for frying) – as needed',
      'Spring onions (for garnish) – chopped',
      'Garlic – 5 cloves',
      'Paprika – 1-2 pcs'
    ],
    steps: [
      'Clean the oyster mushrooms and tear them into bite-sized pieces.',
      'In a bowl, mix cornstarch, salt, black pepper, and Chinese 5 spice.',
      'Coat the mushroom pieces in the cornstarch mixture.',
      'Heat oil in a pan over medium-high heat.',
      'Fry the coated mushrooms until golden and crispy.',
      'Remove and drain on paper towels.',
      'In a separate pan, sauté minced garlic and chopped paprika until fragrant.',
      'Toss the fried mushrooms in the garlic-paprika mixture.',
      'Garnish with chopped spring onions before serving.'
    ],
    imageUrl: '/images/crispy_salt_pepper_oyster_mushrooms.jpg'
  },
  {
    id: '7',
    title: 'Tulsi No-jito',
    subtitle: 'A refreshing Indian twist on the classic mojito, featuring aromatic tulsi (holy basil) leaves.',
    type: 'drink',
    cuisine: 'Indian-Fusion',
    cookingTime: '10 minutes',
    date: '2025-10-15',
    ingredients: [
      'Fresh tulsi (holy basil) leaves – 10-12',
      'Lime juice – 30 ml (about 1 lime)',
      'Sugar syrup/Honey/Agave – 20 ml (adjust to taste)',
      'Cucumber slices – 4-5',
      'Sparkling water – 150 ml',
      'Ice cubes – as needed',
      'Mint leaves – for garnish (optional)'
    ],
    steps: [
      'In a glass, muddle the cucumber slices, tulsi and mint leaves with lime juice and sugar syrup until the leaves release their aroma.',
      'Fill the glass with ice cubes.',
      'Pour in the sparkling water and stir gently to combine.',
      'Garnish with a sprig of tulsi and a lime wedge before serving.'
    ],
    imageUrl: '/images/tulsi_nojito.jpg'
  },
  {
    id: '8',
    title: 'Smashed Pea + Edamame on Toast',
    subtitle: 'A vibrant and nutritious twist on the classic smashed avocado toast, featuring peas and edamame.',
    type: 'food',
    cuisine: 'Fusion',
    cookingTime: '10 minutes',
    date: '2025-10-12',
    ingredients: [
      'Frozen garden peas – 250g',
      'Frozen edamame beans – 250g',
      'Spring onions – 1 bunch',
      'Fresh mint – 15g',
      'Fresh coriander – 15g',
      'Garlic – 1 clove, minced',
      'Lime – juice + zest',
      'Miso paste – 1 tsp',
      'Soy sauce – 1 tsp',
      'Agave syrup – 1 tsp',
      'Silken tofu – 1 block (optional)',
    ],
    steps: [
      'On a baking tray, season the spring onions with salt. Drizzle with oil + cook at 200 degrees celsius for 5-10 mins (until charred + soft).',
      'Blanch the peas + edamame beans in boiling water for 1 min, then transfer to a food processor (reserve 1 tbsp for garnish)',
      'Add the coriander, mint, spring onions, garlic, silken tofu if using, miso paste, soy sauce + agave. Pulse until chunky + combined.',
      'Sautee some shiitake mushrooms in a pan with oil, salt + pepper until golden + cooked through (about 5-7 mins). Set aside.',
      'In the same pan, add some butter and toast your bread slices to your liking. ',
      'Top with the sautéed mushrooms, dip, lime zest, a drizzle of olive oil + a sprinkle of chili flakes if desired. Serve immediately.'
    ],
    imageUrl: '/images/food/Muhsroom Edamame Toast.jpeg'
  },
{
    id: '9',
    title: 'Caramelized Onion Hummus',
    subtitle: 'Take a Dip',
    type: 'food',
    cuisine: 'Middle Eastern',
    cookingTime: '15 minutes',
    date: '2025-10-10',
    ingredients: [
      '(serves 4-6)',
      '4 red onions, sliced',
      '2 tbsp olive oil',
      '1 tbsp butter',
      '1 tbsp balsamic vinegar',
      '400g chickpeas (+ aquafaba)',
      '4 tbsp tahini',
      '1 lemon, juiced',
      '2 garlic cloves, minced',
      '3-4 ice cubes',
      '15g fresh parsley, chopped',
      '1 tsp paprika',
      'Salt + pepper',
      'Dipper of choice'
    ],

    steps: [
       'Melt the butter + oil in a pan. ',
      'Add the sliced onions with a pinch of salt then fry on a low heat, stirring often, until caramelised - minimum 20 mins, but longer the better. ',
      'When the onions begin to catch, add deglaze with balsamic vinegar, then add the garlic for the final 5 mins. Set aside 1 tbsp for garnish.',
      'In a food processor or blender, mix the tahini with the lemon juice until combined.',
      'Drain the chickpeas (reserve their liquid + set aside 1 tbsp for garnish) then add to the processor with 2 tbsp of the liquid & blend again. Add the onions, then blend again.',
      'Whilst the processor runs, add a couple of ice cubes to whip the hummus, making it smooth, fluffy & creamy. Season to taste with salt + pepper.',
      'Plate up the hummus in a bowl, top with the reserved onions + chickpeas, fresh parsley, smoked paprika & enjoy!'
    ]
},
{
    id: '10',
    title: 'Panakam',
    subtitle: 'A traditional South Indian spiced jaggery drink, perfect for cooling down on hot days.',
    type: 'drink',
    cuisine: 'Indian',
    cookingTime: '10 minutes',
    date: '2025-10-08',
    ingredients: [
      'Jaggery – 100g, grated',
      'Water – 1 litre',
      'Black peppercorns – 1 tsp',
      'Cardamom pods – 4, crushed',
      'Edible camphor – a small pinch',
      'Tulsi leaves – for garnish'
    ],
    steps: [
      'In a saucepan, combine the jaggery and water. Heat gently, stirring until the jaggery dissolves completely.',
      'Add the black peppercorns, crushed cardamom pods, and edible camphor. Simmer for 5-7 minutes.',
      'Remove from heat and let it cool slightly. Strain into glasses.',
      'Garnish with tulsi leaves and serve chilled or at room temperature.'
    ]
  },
  {
    id: '11',
    title: 'Gochujang crispy tofu',
    subtitle: 'A spicy and savory Korean-inspired tofu dish that is crispy on the outside and tender on the inside.',
    type: 'food',
    cuisine: 'Korean',
    cookingTime: '25 minutes',
    date: '2025-11-09',
    ingredients: [
      '280g firm tofu (pressed and crumbled)',
      '2 tbsp cornflour',
      'Salt & Pepper',
      '1 tbsp gochugaru (Korean chili pepper flakes)',
      '2 tbsp gochujang (Korean chili paste)',
      '1 tbsp soy sauce',
      '1 tsp vegan oyster sauce',
      '1 tsp sugar',
      '1 tbsp mirin',
      '2 cloves garlic (minced)',
      '2-3 spring onions (sliced)',
    ],
    steps: [
      "Press the tofu for 10-15 minutes to remove excess moisture. Crumble it into small, bite-sized pieces. Add your cornflour then season it with salt and pepper.",
      "Preheat the air fryer to 180°C (350°F). Spread the tofu in a single layer in the basket and air fry for 15-18 minutes, shaking the basket halfway through, until crispy. Alternatively you can use an oven and set 200°C (400°F). Spread the tofu on a lined baking sheet in an even layer. Bake for 20-25 minutes, stirring halfway through, until crispy.",
      "In a bowl, mix the gochugaru, gochujang, soy sauce, oyster sauce, sugar, and mirin until smooth.",
      "Heat a pan over medium heat and add a small amount of oil. Add the minced garlic and the white parts of the spring onions. Sauté for about 1-2 minutes, until fragrant and slightly softened. Remove from heat.",
      "Toss the crumbled tofu into the sauce mixture, making sure it’s evenly coated.",
      "Serve on a plate with rice or have as is :)"
    ],
    imageUrl: '/images/gochujang_crispy_tofu.jpg'
  },
  {
    id: '12',
    title: 'Garlic Tamarind Fried Rice',
    subtitle: 'A spicy, tangy, and buttery fried rice that layers burnt garlic, garlic shoots, red Thai chilies, corn, and edamame, finished with a sweet‑sour tamarind chutney. Burmese‑inspired garlic fried rice meets Indian‑style tamarind flavors in a fast, leftover‑rice friendly dish.',
    type: 'food',
    cuisine: 'Burmese-Indian Fusion',
    cookingTime: '15 minutes',
    date: '2026-04-22',
    ingredients: [
      '2 cups cooked, cold rice (preferably day‑old)',
      '¾ cup shelled edamame (fresh or frozen, thawed)',
      '½ cup corn kernels (fresh, frozen, or canned, drained)',
      '1 cup chopped garlic shoots (white and green parts separated)',
      '4–6 cloves garlic, thinly sliced',
      '2–3 red Thai chilies, finely sliced (adjust to taste)',
      '1–2 tablespoons neutral oil',
      '1–2 tablespoons butter (or more, to taste)',
      '1–2 tablespoons soy sauce, to taste',
      '1–2 tablespoons tamarind chutney',
      '1–3 tablespoons warm water (to thin the chutney)',
      'Salt and black pepper, to taste'
    ],
    steps: [
      '### Prep the rice and tamarind chutney',
      'Break up the cold rice with your hands or a fork so the grains are separated.',
      'In a small bowl, mix the tamarind chutney with warm water to make a smooth, pourable sauce. Adjust thickness by adding more chutney or water as needed.',
      '',
      '### Burn the garlic and start the base',
      'Add neutral oil and the sliced garlic to a large cold pan or wok.',
      'Turn the heat to medium and cook, stirring, until the garlic turns deep golden‑brown with some charred edges.',
      'Scoop out a spoonful of the burnt garlic and set aside for garnish.',
      '',
      '### Add butter and sauté the aromatics',
      'To the pan, add the butter and let it melt into the garlicky oil.',
      'Add the white parts of the garlic shoots and the red Thai chilies.',
      'Sauté for 30–60 seconds until fragrant.',
      '',
      '### Corn and edamame + basic seasoning',
      'Add the corn and edamame to the pan and sauté for 2–3 minutes.',
      'Season lightly with salt and black pepper.',
      '',
      '### Soy sauce simmer, then rice',
      'Pour in the soy sauce and let it simmer with the veggies for 30–60 seconds so the flavors concentrate slightly.',
      'Add the cold rice to the pan and toss well, breaking up any remaining clumps. Stir‑fry for 3–4 minutes until the rice is heated through and lightly fried.',
      '',
      '### Tamarind finish',
      'Pour the thinned tamarind chutney evenly over the rice.',
      'Toss and cook for another 1–2 minutes so the rice is coated and the chutney reduces slightly, making everything glossy, tangy, and spicy.',
      'Add the green parts of the garlic shoots and mix briefly. Taste and adjust salt, pepper, soy sauce, or tamarind chutney as needed.',
      '',
      '### Serve',
      'Turn off the heat. Top with the reserved burnt garlic for crunchy, smoky bites.',
      'Serve hot as a main, or alongside something like chilli tofu or stir‑fried vegetables.'
    ]
  },
  {
    id: '13',
    title: ' "Missi Roti" ',
    subtitle: 'A modern Rajasthani fusion take on the classic Missi Roti. ',
    type: 'food',
    cuisine: 'Modern Rajasthani',
    cookingTime: '40 minutes',
    date: '2026-04-24',
    ingredients: [
      '### For the Buttery Arbi Layer',
      '1 cup Arbi (Taro root), peeled and halved',
      '1 tbsp Ghee for pan-searing',
      'Pinch of sea salt and pepper',
      '',
      '### For the Missi Roti Base',
      '½ cup Besan (Gram flour)',
      '½ cup Atta (Whole wheat flour)',
      '2 tbsp Sooji (Semolina, the secret to making it extra crispy!)',
      '1 tsp Ajwain (Carom seeds)',
      '1 tsp Kasuri methi (Dried fenugreek leaves)',
      '½ tsp Turmeric powder',
      'Salt to taste',
      '1 tbsp Ghee (plus more for cooking)',
      'Water to knead',
      '',
      '### For the Lassan ki Chutney',
      '10-12 Garlic cloves',
      '6-8 Kashmiri red chilies (soaked in warm water for 20 mins)',
      '1 tsp Cumin seeds',
      '2 tbsp Mustard oil',
      'Salt to taste',
      '',
      '### For the Tomato-Kairi Chutney',
      '1 small Kairi (Raw mango), peeled and finely chopped',
      '2 medium Tomatoes, chopped',
      '1 Green chili, chopped',
      '½ tsp Mustard seeds',
      '½ tsp Fennel seeds',
      '1 tbsp Oil',
      'Fresh coriander for garnish'
    ],
    steps: [
      '### Prep the Buttery Arbi ',
      'Boil the Arbi until just fork-tender (do not over-boil as it can turn mushy or slimy).',
      'Drain, cool, and thinly slice the Arbi horizontally (similar to avocado slices).',
      'In a pan, heat 1 tbsp ghee. Gently sear the Arbi slices on both sides until slightly crispy on the outside but still buttery and creamy inside. Season lightly with sea salt and set aside.',
      '',
      '### Make the Lassan ki Chutney',
      'In a blender, combine the garlic cloves, soaked Kashmiri red chilies, cumin seeds, and a pinch of salt. Blend to a smooth paste (use a splash of water if needed).',
      'Heat mustard oil in a pan until it smokes slightly. Turn down the heat and add the chili-garlic paste.',
      'Cook for 4-5 minutes until the oil separates and the raw garlic smell is gone. Set aside.',
      '',
      '### Prepare the Tomato-Kairi Chutney',
      'Heat 1 tbsp oil in a small pan. Temper the mustard and fennel seeds.',
      'Add the green chili, chopped tomatoes, and raw mango (kairi).',
      'Add a pinch of salt and cook down for 8-10 minutes until the tomatoes turn soft and the kairi provides a sweet-tart tanginess.',
      '',
      '### Cook the Missi Roti',
      'In a bowl, mix besan, atta, sooji, ajwain (crushed between palms), kasuri methi, turmeric, salt, and 1 tbsp ghee.',
      'Slowly add water to knead into a very stiff dough (the stiffer the dough, the crispier the roti).',
      'Divide the dough into small balls. Roll them into slightly thick, small discs (toast-sized). *Crucial step*: Prick them all over with a fork (docking) so they don\'t puff up and instead turn into crispy crackers.',
      'Heat a tawa (griddle) on medium-low heat. Slow-roast each roti with ghee, pressing down with a flat spatula until it is completely crisp, golden brown, and biscuit-like.',
      '',
      '### Assembly (The Final "Toast")',
      'Take a warm, crisp Missi Roti as your base.',
      'Spread a generous layer of the fiery Lassan ki Chutney to taste.',
      'Fan out the buttery, pan-seared Arbi slices over the chutney.',
      'Top with a dollop of the sweet-sour Tomato-Kairi chutney.',
      'Garnish with fresh coriander or microgreens, and serve immediately ',]
  }
];